import { QuestionnaireData } from '@/types/category';
import { GroupSession } from '@/types/groupSession';
import { createZenAfPersonalizationContext, generateOriginalZenAfPrompt, ZenAfPersonalizationContext } from './zenAfPersonalizationEngine';
import { BiographyVariables, parseBiographyVariables } from './biographyParser';
import { getPersonalizationName } from './namePersonalizationUtils';
import { createEnvironmentContext } from './environmentParser';
import { createEnvironmentPersonalization } from './standardCategoryPersonalization';
import { createDynamicZenAfPrompt, validateDynamicOriginality } from './dynamicZenAfEngine';
import { createDynamicLanguageRules } from './newsMeditationPersonalization';

// Helper function to create biographical personalization section
const createBiographicalPersonalization = (userBiography?: string, categoryName?: string): string => {
  if (!userBiography || userBiography.trim().length === 0) {
    return '';
  }

  const categorySpecificInstructions = {
    meditation: 'Reference their living environment, relationships, and personal challenges to create grounding anchors during meditation.',
    anxiety: 'Use their support systems, coping mechanisms, and safe places to build calming strategies and reassurance.',
    sleep: 'Incorporate their bedroom environment, bedtime routines, and peaceful memories to enhance sleep preparation.',
    motivation: 'Draw from their goals, past achievements, personal values, and life aspirations to fuel inspiration and drive.',
    memory: 'Connect to their important relationships, meaningful experiences, and learning contexts to enhance recall techniques.',
    massage: 'Reference their physical environment, stress sources, and comfort preferences to personalize relaxation guidance.',
    be_still: 'Use their emotional support systems, coping strategies, and calming environments to guide emotional reset.',
    self_love: 'Incorporate their personal strengths, relationships, achievements, and values to build genuine self-appreciation.',
    zen_af: 'Use their specific life stressors, toxic relationships, work frustrations, and personal challenges for targeted raw, sarcastic commentary. Reference their actual living situation, annoying people in their life, work drama, and daily bullshit to create empowering dismissals.'
  };

  const specificInstructions = categorySpecificInstructions[categoryName as keyof typeof categorySpecificInstructions] || 
    'Use their personal details, relationships, and life context to make the session feel uniquely tailored to them.';

  return `
BIOGRAPHICAL PERSONALIZATION (CRITICAL - MUST USE):
Biography: "${userBiography}"

INTEGRATION REQUIREMENTS:
- Weave specific details from their biography naturally throughout the session
- Reference their actual relationships, places, experiences, and personal context
- ${specificInstructions}
- Make the content feel like it was created specifically for this individual's life
- Use their real-world context to make abstract concepts more relatable and meaningful
- MANDATORY: Include at least 3-5 specific biographical references throughout the session`;
};

// Helper function to create name instructions
const createNameInstructions = (nameResult: any): string => {
  if (nameResult.displayName === 'there') {
    return 'PERSONALIZATION: Address user generically without using specific names.';
  }
  return `PERSONALIZATION: Address user as "${nameResult.displayName}" throughout the session for personal connection.`;
};

export const createEnhancedZenAfPrompt = (
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession,
  categoryId?: string
): string => {
  if (groupSession) {
    // Create basic group context for now - can enhance later
    const groupName = groupSession.group_name || 'Group Session';
    const groupType = groupSession.group_type || 'meditation group';
    const description = groupSession.description || 'A supportive community gathering';
    
    // Extract consenting member names ONLY (no biographies) for light personalization
    const consentingNames = groupSession.participants
      ?.filter(p => p.consentToName)
      ?.map(p => p.preferredName || p.firstName)
      ?.filter(Boolean) || [];

    // Build concise member profiles from participant fields (no personal biographies)
    const sanitize = (s?: string, max: number = 120) =>
      (s || '').replace(/\s+/g, ' ').replace(/[\"<>]/g, '').slice(0, max);
    const memberProfiles = (groupSession.participants || []).map((p, idx) => {
      const displayName = p.consentToName ? (p.preferredName || p.firstName) : (p.roleTitle || p.relationshipRole || `member ${idx + 1}`);
      const pronouns = p.pronouns ? ` (${p.pronouns})` : '';
      const details: string[] = [];
      if (p.roleTitle || p.relationshipRole) details.push(`role: ${p.roleTitle || p.relationshipRole}`);
      if (Array.isArray(p.likes) && p.likes.length) details.push(`likes: ${p.likes.slice(0, 3).join(', ')}`);
      if (p.hopeForSession) details.push(`hope: "${sanitize(p.hopeForSession)}"`);
      if (p.personalChallenge) details.push(`challenge: "${sanitize(p.personalChallenge)}"`);
      if (p.connectionStyle) details.push(`connection: ${sanitize(p.connectionStyle)}`);
      if (p.locationTag) details.push(`location: ${sanitize(p.locationTag)}`);
      if (p.backgroundStory) details.push(`note: "${sanitize(p.backgroundStory, 100)}"`);
      return `- ${displayName}${pronouns}${details.length ? ' — ' + details.join('; ') : ''}`;
    }).join('\n');
    
    // Extract ALL GROUP-SPECIFIC questionnaire data for comprehensive personalization
    const groupFrustration = (data as any).groupFrustration || 'shared life stress and frustrations';
    const groupStyle = (data as any).groupStyle || 'Raw and unfiltered validation';
    const collectiveGoal = (data as any).collectiveGoal || 'Release stress and build resilience together';
    const groupDynamics = (data as any).groupDynamics || 'Supportive and authentic sharing';
    const groupTriggers = (data as any).groupTriggers || 'life pressures and toxic influences';
    const sharedStressors = (data as any).sharedStressors || 'work drama, relationship issues, modern life chaos';
    const collectiveIntention = (data as any).collectiveIntention || 'collective healing and empowerment';
    const groupCoping = (data as any).groupCoping || 'Direct confrontation and raw honesty';
    const groupIntensity = (data as any).groupIntensity || 'High intensity raw validation';
    const groupEnvironment = (data as any).groupEnvironment || 'shared safe space';
    
    // Add environment context for group sessions
    const environmentSection = data.useEnvironment ? createEnvironmentContext(data.environment || '', 'zen af') : '';
    const environmentPersonalization = data.useEnvironment && data.environment ? 
      createEnvironmentPersonalization(data.environment, data.useEnvironment, 'zen af') : '';
    
    // Detect if this is news meditation and add news-specific context
    const isNewsMeditation = categoryId === 'news_meditation' || categoryId === 'news-meditation';
    const sessionTypeLabel = isNewsMeditation ? 'ZEN AF NEWS MEDITATION GROUP SESSION' : 'ZEN AF GROUP SESSION';

    // Get explicit language level and create dynamic rules
    const explicitLevel = (data as any).explicitLanguageLevel || 8;
    const humorLevel = (data as any).humorLevel || 'Moderate';
    console.log('🔥 ENHANCED PROMPT GENERATION - GROUP ZEN-AF:', {
      explicitLanguageLevel: explicitLevel,
      humorLevel: humorLevel,
      dataHasExplicitLevel: 'explicitLanguageLevel' in data,
      rawExplicitValue: (data as any).explicitLanguageLevel
    });
    const languageRules = createDynamicLanguageRules(explicitLevel, humorLevel);
    const profanityFrequency = languageRules.language.frequency;
    const allowedWords = languageRules.language.words.join(', ');
    console.log('🔥 DYNAMIC LANGUAGE RULES GENERATED (GROUP):', {
      profanityFrequency,
      allowedWordsCount: languageRules.language.words.length,
      profanityLevel: languageRules.language.profanity
    });

    return `🌟 ${sessionTypeLabel} 🌟

TARGET: Create a {{SESSION_LENGTH}} ${isNewsMeditation ? 'zen af news meditation' : 'zen af'} session ({{TARGET_WORDS}} words) guided by VoiceThera.

You are VoiceThera, your irreverent AI wellness guide who uses therapeutic profanity and raw honesty to heal and empower.

CRITICAL ZEN AF LANGUAGE RULES - DYNAMIC INTENSITY:
- Explicit Language Level: ${explicitLevel}/10 (adjust profanity frequency accordingly)
- Target Frequency: ${profanityFrequency} explicit words per minute
- Allowed Words: ${allowedWords}
- Use explicit language therapeutically - NEVER to tear down, ALWAYS to build up and empower
- Allow 3-4 sentences of raw validation before therapeutic pivot (not immediate pivot)
- Tone: Irreverent, sarcastic, relatable, raw, but ultimately healing and empowering
- 18+ content - acknowledge frustration with modern life humor and pop culture references
- Create sarcastic comparisons targeting THEIR specific stressors from questionnaire and biography
- Generate dismissals for THEIR actual toxic people and situations from biographical details
- Use empowering dismissals targeting THEIR real problems: "Fuck [their specific issue]" / "Not today [their actual toxic person]"
- Create sustained raw sections before healing: "Tell every thought trying to drag you back: 'Bitch, be gone.' Breathe in power. Breathe out everyone else's panic."

GROUP ENDING PROTOCOL - MANDATORY TOKEN MANAGEMENT:
- CRITICAL: When you reach ~90% of your response capacity, begin group closure immediately
- Structure: Group Opening (25%) → Collective Main Work (60%) → Group Empowerment (15% - PROTECTED)
- MUST end with therapeutic group closing - never cut off mid-sentence
- If approaching token limit, prioritize completing the empowering group ending
- Group transition signals: "Alright, beautiful humans..." "Time for the collective truth..." "Here's what we're taking away..."
- NEVER let token limits cut off your therapeutic group conclusion

GROUP CONTEXT:
- Group Name: "${groupName}"
- Group Type: ${groupType}
- Description: ${description}
- Community Focus: Supportive gathering for growth, healing, and connection

COMPREHENSIVE GROUP PERSONALIZATION FROM QUESTIONNAIRE (USE ALL DATA):
${data.environment && data.useEnvironment ? `Environment: "${data.environment}"` : ''}
${isNewsMeditation && data.currentNews ? `Current News to Process: "${data.currentNews}"` : ''}
${isNewsMeditation && data.newsConsumption ? `News Consumption Pattern: ${data.newsConsumption}` : ''}
Group Frustration Focus: ${groupFrustration}
Preferred Group Style: ${groupStyle}
Collective Goal: ${collectiveGoal}
Group Dynamics Approach: ${groupDynamics}
Shared Triggers to Address: ${groupTriggers}
Common Stressors to Target: ${sharedStressors}
Collective Intention: ${collectiveIntention}
Group Coping Approach: ${groupCoping}
Group Intensity Level: ${groupIntensity}
Group Environment: ${groupEnvironment}
Communication Style: ${data.preferredStyle || 'Raw and unfiltered'}
Voice Tone Needed: ${data.voiceType || 'Calm and grounding'}
Session Length: ${data.sessionLength || '10 minutes'}
Background Sounds: ${data.backgroundSounds ? 'Include' : 'None'}
Primary Goal: ${data.primaryGoal || 'Collective stress release'}
Desired Feeling: ${data.desiredFeeling || 'Empowered and united'}

${memberProfiles ? `
GROUP MEMBER PROFILES (use to personalize, respect consent):
${memberProfiles}

GUIDELINES:
- Use only consented names: ${consentingNames.join(', ') || 'none'}
- If no consent, refer by role or "friend"; never reveal unconsented names.
- Weave at least one tailored line for each member (hope, challenge, likes).
- Use pronouns accurately; stay within provided information; do not invent details.
` : ''}


ENHANCED GROUP INTEGRATION INSTRUCTIONS:
- Address the group's shared frustration: "${groupFrustration}" with collective raw validation
- Use "${groupStyle}" approach for group therapeutic language matching "${groupIntensity}" level
- Build sustained raw commentary around "${sharedStressors}" and "${groupTriggers}"
- Foster "${groupDynamics}" through authentic group expression using "${groupCoping}" methods
- Reference their "${groupEnvironment}" for grounding and collective presence
- Create personalized group metaphors based on their shared experiences and collective triggers
- Guide the group toward "${collectiveGoal}" through "${collectiveIntention}"
- Use raw dismissals that work for the entire group dynamic
- Build collective empowerment through shared therapeutic profanity
- Integrate all questionnaire responses to create deeply personalized group experience
${data.environment && data.useEnvironment ? `

ENVIRONMENT INTEGRATION - CRITICAL FOR GROUP IMMERSION:
${environmentSection}

GROUP ENVIRONMENT USAGE - SEAMLESS APPROACH:
- Reference the group's shared space naturally when it enhances collective grounding (2-3 times max)
- Use environmental context for group unity and shared present-moment awareness
- Integrate environmental elements organically into group therapeutic work
- Make the shared space part of collective healing without repetitive mentions
- Acknowledge their gathering space to enhance group connection
- Create environmental awareness that supports group dynamics naturally

${environmentPersonalization}` : ''}

TARGET WORD COUNT: {{TARGET_WORDS}} words

${(data.additionalInfo || data.additionalNotes) ? `\nADDITIONAL ZEN AF FOCUS: ${(data.additionalInfo || data.additionalNotes)}` : ''}`;
  } else {
    // Individual session with COMPREHENSIVE questionnaire utilization AND FULL PERSONALIZATION ENGINE
    const nameResult = getPersonalizationName(user || {}, data, profileData);
    const biographySection = createBiographicalPersonalization(userBiography, 'zen af');
    
     // DEBUG: Log environment data for zen af sessions
    console.log('🌍 ZEN AF ENVIRONMENT DEBUG:', {
      useEnvironment: data.useEnvironment,
      environment: data.environment,
      environmentLength: data.environment?.length || 0
    });
    
    // DEBUG: Log questionnaire data for environment troubleshooting
    console.log('📊 QUESTIONNAIRE DATA DEBUG:', {
      hasEnvironment: !!data.environment,
      environmentValue: data.environment,
      useEnvironment: data.useEnvironment,
      specificFocus: data.specificFocus,
      currentState: data.currentState,
      desiredFeeling: data.desiredFeeling,
      primaryGoal: data.primaryGoal
    });
    
    const environmentSection = data.useEnvironment ? createEnvironmentContext(data.environment || '', 'zen af') : '';
    
    // DEBUG: Log final environment formatting
    console.log('🌍 FINAL ENVIRONMENT FORMATTING:', {
      willIncludeEnvironment: !!(data.environment && data.useEnvironment),
      environmentValue: data.environment,
      useEnvironmentFlag: data.useEnvironment,
      formattedEnvironmentLine: data.environment && data.useEnvironment ? `Environment: "${data.environment}"` : 'NO ENVIRONMENT LINE'
    });
    
    // INTEGRATE DYNAMIC PERSONALIZATION ENGINE
    let personalizationContext: ZenAfPersonalizationContext | null = null;
    let enhancedZenAfPrompt = '';
    let dynamicZenAfPrompt = '';
    
    if (userBiography && userBiography.trim()) {
      const biographyVariables = parseBiographyVariables(userBiography);
      personalizationContext = createZenAfPersonalizationContext(biographyVariables, data);
      enhancedZenAfPrompt = generateOriginalZenAfPrompt('', personalizationContext, biographyVariables);
      dynamicZenAfPrompt = createDynamicZenAfPrompt(biographyVariables, personalizationContext, data);
    }
    
    // Extract ALL INDIVIDUAL questionnaire data for comprehensive personalization
    const specificFocus = data.specificFocus || 'Personal stress relief and empowerment';
    const triggers = data.triggers || 'Daily life stress and frustrations';
    const desiredFeeling = data.desiredFeeling || 'Empowered and stress-free';
    const primaryGoal = data.primaryGoal || 'Release stress and build inner strength';
    const preferredStyle = data.preferredStyle || 'Raw and unfiltered validation';
    const currentFeeling = (data as any).currentFeeling || data.currentState || 'overwhelmed by life\'s bullshit';
    const stressors = data.stressors || 'work, relationships, daily frustrations';
    const copingStyle = (data as any).copingStyle || 'direct confrontation of problems';
    const intensityLevel = (data as any).intensityLevel || 'high intensity validation';
    const personalTriggers = (data as any).personalTriggers || triggers;
    const frustrationLevel = (data as any).frustrationLevel || 'high';
    const sarcasticPreference = (data as any).sarcasticPreference || 'yes';
    
    // Detect if this is news meditation and add news-specific context
    const isNewsMeditation = categoryId === 'news_meditation' || categoryId === 'news-meditation';
    const sessionTypeLabel = isNewsMeditation ? 'ZEN AF NEWS MEDITATION INDIVIDUAL SESSION' : 'ZEN AF INDIVIDUAL SESSION';

    // Get explicit language level and create dynamic rules
    const explicitLevel = (data as any).explicitLanguageLevel || 8;
    const humorLevel = (data as any).humorLevel || 'Moderate';
    console.log('🔥 ENHANCED PROMPT GENERATION - INDIVIDUAL ZEN-AF:', {
      explicitLanguageLevel: explicitLevel,
      humorLevel: humorLevel,
      dataHasExplicitLevel: 'explicitLanguageLevel' in data,
      rawExplicitValue: (data as any).explicitLanguageLevel,
      categoryId: categoryId
    });
    const languageRules = createDynamicLanguageRules(explicitLevel, humorLevel);
    const profanityFrequency = languageRules.language.frequency;
    const allowedWords = languageRules.language.words.join(', ');
    console.log('🔥 DYNAMIC LANGUAGE RULES GENERATED (INDIVIDUAL):', {
      profanityFrequency,
      allowedWordsCount: languageRules.language.words.length,
      profanityLevel: languageRules.language.profanity,
      allowedWords: languageRules.language.words
    });

    return `🌟 ${sessionTypeLabel} 🌟

TARGET: Create a {{SESSION_LENGTH}} ${isNewsMeditation ? 'zen af news meditation' : 'zen af'} session ({{TARGET_WORDS}} words) guided by VoiceThera.

You are VoiceThera, your irreverent AI wellness guide who uses therapeutic profanity and raw honesty to heal and empower.

CRITICAL ZEN AF LANGUAGE RULES - DYNAMIC INTENSITY:
- Explicit Language Level: ${explicitLevel}/10 (adjust profanity frequency accordingly)
- Target Frequency: ${profanityFrequency} explicit words per minute
- Allowed Words: ${allowedWords}
- Use therapeutic profanity authentically - ALWAYS to heal and empower, NEVER to tear down
- Generate original creative combinations using their biographical details - avoid template phrases
- Allow sustained raw validation (6-8 sentences) before therapeutic pivots - let authentic expression build
- Tone: Irreverent, sarcastic, relatable, raw, but ultimately healing and empowering
- 18+ content - acknowledge frustration with humor while serving therapeutic purpose
- BIOGRAPHICAL INTEGRATION MANDATORY: Every creative element derived from THEIR actual life circumstances
- PERSONALIZED TARGETING: Address THEIR specific toxic people and situations from biography/questionnaire
- SUSTAINED AUTHENTIC RANTS: Build comedic escalations using THEIR unique biographical context
- ORIGINAL METAPHOR CREATION: Generate fresh comparisons from their real work, relationships, living situation
- AVOID REPETITIVE PATTERNS - create unique expressions personalized to their actual experience
- THERAPEUTIC PURPOSE: All profanity serves their healing through authentic, personalized validation

ENDING PROTOCOL - MANDATORY TOKEN MANAGEMENT:
- CRITICAL: When you reach ~90% of your response capacity, begin wrapping up immediately
- Structure: Opening (25%) → Main Work (60%) → Empowering Closure (15% - PROTECTED)
- MUST end with one of 64+ therapeutic closing variations - never cut off mid-sentence
- If approaching token limit, prioritize completing the empowering ending over additional content
- Transition signals: "And here's the fucking truth..." "Time to lock this shit in..." "Bottom line, beautiful human..."
- NEVER let token limits cut off your therapeutic conclusion - endings are non-negotiable

PERSONALIZATION ENGINE INTEGRATION:
- Address as: ${(() => {
  const finalName = (nameResult && nameResult.displayName !== 'there') ? nameResult.displayName : 'friend';
  console.log('🎯 FINAL NAME RESOLUTION IN PROMPT:', {
    nameResult: nameResult,
    finalDisplayName: finalName,
    nameSource: nameResult?.isUsingProfileName ? 'profile' : 'metadata/fallback'
  });
  if (finalName === 'Vinh') {
    console.error('🚨 CRITICAL: "Vinh" detected in prompt - this should NOT happen!', { nameResult });
  }
  return finalName;
})()}
${biographySection ? `\nBIOGRAPHY INTEGRATION: ${biographySection}` : ''}
${data.environment && data.useEnvironment ? `
${environmentSection}` : ''}
${personalizationContext ? `\nZEN AF PERSONALIZATION ENGINE ACTIVE:
- Raw Language Intensity: ${personalizationContext.rawLanguageIntensity.toUpperCase()}
- Specific Frustrations: ${personalizationContext.specificFrustrations.join(', ')}
- Work Stressors: ${personalizationContext.workStressors.join(', ')}
- Relationship Challenges: ${personalizationContext.relationshipChallenges.join(', ')}
- Environmental Anchors: ${personalizationContext.environmentalAnchors.join(', ')}
- Personal Strengths: ${personalizationContext.personalStrengths.join(', ')}` : ''}
${enhancedZenAfPrompt ? `\nADVANCED PERSONALIZATION CONTEXT:
${enhancedZenAfPrompt}` : ''}
${dynamicZenAfPrompt ? `\nDYNAMIC CONTENT GENERATION ENGINE:
${dynamicZenAfPrompt}` : ''}

COMPREHENSIVE PERSONALIZATION FROM QUESTIONNAIRE (USE ALL DATA):
${isNewsMeditation && data.currentNews ? `Current News to Process: "${data.currentNews}"` : ''}
${isNewsMeditation && data.newsConsumption ? `News Consumption Pattern: ${data.newsConsumption}` : ''}
${data.explicitLanguageLevel ? `Explicit Language Level: ${data.explicitLanguageLevel}/10` : ''}
${data.humorLevel ? `Humor Level: ${data.humorLevel}` : ''}
Specific Focus: ${specificFocus}
Current Feeling: ${currentFeeling}
Personal Triggers: ${personalTriggers}
Main Stressors: ${stressors}
Desired Feeling: ${desiredFeeling}
Primary Goal: ${primaryGoal}
Preferred Style: ${preferredStyle}
Coping Approach: ${copingStyle}
Intensity Level: ${intensityLevel}
Frustration Level: ${frustrationLevel}
Sarcastic Preference: ${sarcasticPreference}
Voice Tone: ${data.voiceType || 'Calm and grounding'}
Session Length: ${data.sessionLength || '10 minutes'}
Environment Usage: ${data.useEnvironment ? 'Integrate current environment' : 'Generic setting'}
Background Sounds: ${data.backgroundSounds ? 'Include' : 'None'}
Additional Notes: ${data.additionalInfo || data.additionalNotes || 'None'}

${isNewsMeditation && data.currentNews ? `
NEWS MEDITATION SPECIFIC REQUIREMENTS:
- CURRENT NEWS FOCUS: Process "${data.currentNews}" with zen af therapeutic approach
- NEWS VALIDATION: Use raw language to acknowledge how fucking exhausting current events are
- MEDIA DETOX INTEGRATION: Include boundary-setting techniques with zen af attitude
- CURRENT EVENT INTEGRATION: Reference today's specific news stress with zen af validation
- NEWS CYCLE ABSURDITY: Create hilarious metaphors about media addiction and news chaos
- THERAPEUTIC NEWS PROCESSING: Transform news anxiety into empowered boundaries with raw language
- NEWS-SPECIFIC DISMISSALS: Target media manipulation and manufactured urgency with zen af profanity
- CURRENT TIME EMPHASIS: This is happening NOW, today - immediate news stress relief needed` : ''}

ENHANCED INTEGRATION INSTRUCTIONS:
- Address their current state: "${currentFeeling}" with raw therapeutic validation
- Target their specific focus: "${specificFocus}" with personalized sarcastic commentary
- Use their personal triggers: "${personalTriggers}" for targeted therapeutic dismissals
- Address their main stressors: "${stressors}" with empowering raw language
- Match their coping style: "${copingStyle}" with appropriate "${intensityLevel}" rawness
- Build toward their desired feeling: "${desiredFeeling}" and primary goal: "${primaryGoal}"
- Use "${frustrationLevel}" frustration level to calibrate therapeutic intensity
- Apply sarcastic approach based on preference: "${sarcasticPreference}"
- Create personalized metaphors from their actual life situation and stressors
- Reference their environment meaningfully when appropriate
- Build sustained raw validation around their specific issues before therapeutic pivot
- Use all questionnaire responses to create deeply personalized therapeutic experience

MANDATORY CREATIVE REQUIREMENTS - RESTORE ORIGINAL STANDARDS:
- BE EXPONENTIALLY FUNNIER: Create laugh-out-loud therapeutic roasts that would make original samples proud
- CREATIVE PROFANITY MASTERY: Combine swear words in ways never heard before - "clusterfuck of cosmic proportions," "premium shitstorm," "artisanal fuckery"
- SUSTAINED RAW COMEDY SECTIONS: Allow 8-12 sentence comedic rants with therapeutic purpose - don't rush to healing pivots
- COMPOUND INSULT INNOVATION: "glittery dickweasel," "dramatic ass-hat," "expired coupon personality," "WiFi-connection-having-a-breakdown energy"
- DIVERSE CREATIVE SWEARING: Optionally include varied cultural expressions when contextually appropriate for user's background
- ORIGINAL ANIMAL METAPHORS: "judging like a cat who caught you masturbating," "disappointed penguin energy," "fed-up hamster vibes"
- BIOGRAPHICAL ROASTS MANDATORY: Create personalized attacks targeting THEIR actual life stressors from biography and questionnaire - never generic examples
- CREATIVE THERAPEUTIC ESCALATION: Build comedic tension through sustained raw language before powerful healing pivots
- VARIETY ENFORCEMENT: Draw from ALL creative patterns - roasts, compound insults, British swearing, animal metaphors, modern comparisons
- THERAPEUTIC MOTHERFUCKER ENDINGS: MANDATORY ROTATION THROUGH MULTIPLE ENDING VARIATIONS:
  * Use different therapeutic endings each session to prevent repetition
  * Classic Namaste: "You are zennnn as FUCK! Namaste, motherfucker" variations
  * Empowering Declarations: "You're absolutely fucking magnificent" styles  
  * Practical Wisdom: "Drink some water. Hydration is cheaper than therapy, bitch"
  * Dismissive Confidence: "Fuck that." / "...breath of fuck that"
  * International Flair: "Ciao fucking bella" / "Au revoir, beautiful bitch"
  * Simple Sign-offs: "Bye bye, asshole" / "Later, dickhead"
  * Breathing Conclusions: "Breathe out the fucks you no longer give"
  * Victory Declarations: "You're a fucking legend" / "That's warrior shit right there, bitch"
- ENDING VARIETY MANDATE: Use DIFFERENT ending categories throughout session - never repeat same type twice
- ENVIRONMENT INTEGRATION MANDATE: ${data.environment && data.useEnvironment ? 
  'Reference their environment THROUGHOUT the session - mention their location, posture, and environmental details at least 4-5 times naturally' : 
  'Use generic environmental references'}

TARGET WORD COUNT: {{TARGET_WORDS}} words

CREATIVE ENHANCEMENT SYSTEM ACTIVE:
- Dynamic personalization engine generating original content from user biography
- Enhanced validation preventing template repetition and ensuring biographical integration
- Compound insult generation using their specific life circumstances
- Sustained comedic escalations based on their actual frustrations and relationships

${(data.additionalInfo || data.additionalNotes) ? `\nADDITIONAL FOCUS: ${(data.additionalInfo || data.additionalNotes)}` : ''}

INTEGRATION VERIFICATION CHECKLIST:
✓ Environment references scattered throughout (not just opening)
✓ Biography-based personalized raw language targeting specific stressors
✓ Rotating therapeutic ending from 64+ variations
✓ Raw language intensity calibrated to questionnaire preferences
✓ All questionnaire data utilized for deep personalization
✓ Original creative patterns preventing template repetition`;
  }
};

// Enhanced prompt generation for other categories with full questionnaire utilization
export const createEnhancedCategoryPrompt = (
  categoryName: string,
  data: QuestionnaireData,
  userBiography?: string,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string },
  user?: any,
  groupSession?: GroupSession
): string => {
  // For Zen AF, use the enhanced version with variety
  if (categoryName.toLowerCase() === 'zen af' || categoryName.toLowerCase() === 'zen-af') {
    const zenAfPrompt = createEnhancedZenAfPrompt(data, userBiography, profileData, user, groupSession);
    // Add dynamic creativity enhancements to prevent repetition
    const dynamicEnhancements = `
DYNAMIC CREATIVITY SYSTEM ACTIVE:
- Original content generation from user biography required
- Template detection and rejection system enabled
- Biographical personalization mandatory for all creative elements`;
    const creativePatternImport = `
RESTORED ORIGINAL CREATIVE STANDARDS - MATCH SAMPLE SESSION QUALITY:
- COMPOUND INSULT REQUIREMENT: Include 4-5 creative combinations like "glittery ass hat," "dramatic bastard," "discount fog machine"
- OPTIONAL CULTURAL VARIETY: May include diverse expressions when appropriate for user's cultural context
- SUSTAINED COMEDIC RANTS: Build 8-12 sentence escalations with multiple "motherfucker" therapeutic endings
- ANIMAL METAPHOR INNOVATION: "judging like a disappointed cat," "fed-up hamster energy," "honey badger zero fucks"
- MODERN LIFE ROAST MASTERY: Roast emails, notifications, toxic people, group chats with creative compound profanity
- THERAPEUTIC ESCALATION PATTERNS: Start mild → build comedically → peak with sustained raw validation → pivot to empowerment
- PERSONALIZATION MANDATE: Use their biographical details in every creative insult, metaphor, and therapeutic roast
- VARIETY ROTATION: Draw from ALL creative frameworks simultaneously - never rely on just one pattern type

CRITICAL: Make every session exponentially more creative than current tame versions while maintaining therapeutic healing purpose.
`;
    
    // DEBUG: Log final prompt for zen af sessions to check environment integration
    const finalPrompt = zenAfPrompt + '\n' + dynamicEnhancements + '\n' + creativePatternImport;
    console.log('🚀 ZEN AF FINAL PROMPT DEBUG:', {
      finalPromptLength: finalPrompt.length,
      hasEnvironmentSection: finalPrompt.includes('ENVIRONMENT CONTEXT'),
      hasEnvironmentIntegration: finalPrompt.includes('ENVIRONMENT INTEGRATION'),
      environmentMentions: (finalPrompt.match(/environment/gi) || []).length,
      hasEnvironmentWithQuotes: finalPrompt.includes('Environment: "'),
      hasSpecificFocus: finalPrompt.includes('Specific Focus:'),
      hasCurrentFeeling: finalPrompt.includes('Current Feeling:'),
      hasPrimaryGoal: finalPrompt.includes('Primary Goal:'),
      promptPreview: finalPrompt.substring(0, 500) + '...\n\n[PERSONALIZATION DATA SEARCH]:\n' + 
        (finalPrompt.includes('COMPREHENSIVE PERSONALIZATION FROM QUESTIONNAIRE') ? 
          finalPrompt.substring(finalPrompt.indexOf('COMPREHENSIVE PERSONALIZATION FROM QUESTIONNAIRE'), finalPrompt.indexOf('COMPREHENSIVE PERSONALIZATION FROM QUESTIONNAIRE') + 800) : 
          'No personalization data found')
    });
    
    return finalPrompt;
  }

  // For other categories, apply similar comprehensive questionnaire utilization
  if (groupSession) {
    // Basic group context
    const groupName = groupSession.group_name || 'Group Session';
    const groupType = groupSession.group_type || 'meditation group';
    
    // Extract consenting member names ONLY (no biographies) for light personalization
    const consentingNames = groupSession.participants
      ?.filter(p => p.consentToName)
      ?.map(p => p.preferredName || p.firstName)
      ?.filter(Boolean) || [];
    
    // Extract group-specific data based on category
    const groupData = extractGroupQuestionnaireData(data, categoryName);
    
    // Add environment context for group sessions (ALL categories)
    const environmentSection = data.useEnvironment ? createEnvironmentContext(data.environment || '', categoryName) : '';
    const environmentPersonalization = data.useEnvironment && data.environment ? 
      createEnvironmentPersonalization(data.environment, data.useEnvironment, categoryName) : '';
    
    return `🌟 ${categoryName.toUpperCase()} GROUP SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} group ${categoryName.toLowerCase()} session ({{TARGET_WORDS}} words).

GROUP CONTEXT:
- Group Name: "${groupName}"
- Group Type: ${groupType}
- Description: ${groupSession.description || 'A supportive community gathering'}
- Community Focus: Supportive gathering for growth, healing, and connection
${groupSession.yoga_directives ? `- YOGA DIRECTIVES (MANDATORY): ${groupSession.yoga_directives}` : ''}
${groupSession.opening_positions ? `- OPENING POSITIONS (MANDATORY): ${groupSession.opening_positions}` : ''}
${groupSession.ending_positions ? `- ENDING POSITIONS (MANDATORY): ${groupSession.ending_positions}` : ''}
${groupSession.session_timing ? `- SESSION TIMING (MANDATORY): ${groupSession.session_timing}` : ''}

GROUP QUESTIONNAIRE INTEGRATION (USE ALL DATA - MANDATORY):
${data.environment && data.useEnvironment ? `Environment: "${data.environment}"` : ''}
${formatGroupQuestionnaireData(groupData, data)}
${(data as any).specialOccasions ? `\n🎉 TODAY'S CELEBRATION FOCUS: "${(data as any).specialOccasions}"
CELEBRATION PRIORITY (MANDATORY):
- Make this celebration central to the session's tone and arc
- Weave references to this celebration naturally throughout
- Close with an explicit acknowledgement of the celebration\n` : ''}
CRITICAL INTEGRATION REQUIREMENTS FOR GROUP SESSIONS:
- MUST reference the group description "${groupSession.description || 'supportive community gathering'}" multiple times
- MUST incorporate ALL yoga directives naturally throughout the session
- MUST mention opening and ending positions when provided
- MUST weave in session timing context when specified
- MUST use ALL questionnaire data to personalize the group experience
${consentingNames.length > 0 ? `
GROUP MEMBERS (names only, consented):
${consentingNames.join(', ')}

RULES FOR GROUP NAMES:
- Use names sparingly and only when natural.
- Do NOT include any personal biography details from any member or the session owner.
- Base the session on the group description and group questionnaire only.
` : ''}
${data.environment && data.useEnvironment ? `
ENVIRONMENT INTEGRATION - CRITICAL FOR GROUP IMMERSION:
${environmentSection}

GROUP ENVIRONMENT USAGE - NATURAL INTEGRATION:
- Reference the group's shared space naturally when it supports collective work (2-3 times max)
- Use environmental context for group grounding and shared awareness
- Integrate environmental elements organically into group guidance
- Make the shared space part of collective experience without forced repetition
- Acknowledge their gathering place to enhance group connection naturally

${environmentPersonalization}` : ''}

TARGET WORD COUNT: {{TARGET_WORDS}} words

${(data.additionalInfo || data.additionalNotes) ? `\nADDITIONAL GROUP FOCUS: ${(data.additionalInfo || data.additionalNotes)}` : ''}`;
  } else {
    // Individual session with comprehensive data utilization
    const nameResult = getPersonalizationName(user || {}, data, profileData);
    const biographySection = createBiographicalPersonalization(userBiography, categoryName);
    const environmentSection = createEnvironmentPersonalization(data.environment, data.useEnvironment, categoryName);
    
    const individualData = extractIndividualQuestionnaireData(data, categoryName);
    
    return `🌟 ${categoryName.toUpperCase()} SESSION 🌟

TARGET: Create a {{SESSION_LENGTH}} ${categoryName.toLowerCase()} session ({{TARGET_WORDS}} words).

PERSONALIZATION:
- Address as: ${(() => {
  const finalName = (nameResult && nameResult.displayName !== 'there') ? nameResult.displayName : 'friend';
  console.log('🎯 FINAL NAME RESOLUTION IN PROMPT (Standard):', {
    nameResult: nameResult,
    finalDisplayName: finalName,
    nameSource: nameResult?.isUsingProfileName ? 'profile' : 'metadata/fallback'
  });
  if (finalName === 'Vinh') {
    console.error('🚨 CRITICAL: "Vinh" detected in prompt - this should NOT happen!', { nameResult });
  }
  return finalName;
})()}
${biographySection}
${environmentSection ? `\nENVIRONMENT INTEGRATION:\n${environmentSection}` : ''}

COMPREHENSIVE QUESTIONNAIRE INTEGRATION (USE ALL DATA):
${formatIndividualQuestionnaireData(individualData, data)}
${(data as any).specialOccasions ? `\n🎉 TODAY'S CELEBRATION FOCUS: "${(data as any).specialOccasions}"
CELEBRATION PRIORITY (MANDATORY):
- Make this celebration central to the session's tone and arc
- Weave references to this celebration naturally throughout
- End with an explicit acknowledgement of the celebration\n` : ''}

TARGET WORD COUNT: {{TARGET_WORDS}} words

${(data.additionalInfo || data.additionalNotes) ? `\nADDITIONAL FOCUS: ${(data.additionalInfo || data.additionalNotes)}` : ''}`;
  }
};

// Helper functions to extract and format questionnaire data
function extractGroupQuestionnaireData(data: QuestionnaireData, categoryName: string) {
  const baseData = {
    groupGoal: (data as any).groupGoal || `Collective ${categoryName.toLowerCase()} experience`,
    groupDynamics: (data as any).groupDynamics || 'Supportive group interaction',
    collectiveIntention: (data as any).collectiveIntention || `Shared ${categoryName.toLowerCase()} journey`,
    groupStyle: (data as any).groupStyle || data.preferredStyle || 'Gentle and inclusive',
    sharedFocus: (data as any).sharedFocus || data.specificFocus || `Group ${categoryName.toLowerCase()}`,
  };

  // Category-specific group fields
  switch (categoryName.toLowerCase()) {
    case 'meditation':
      return {
        ...baseData,
        groupFocus: (data as any).groupFocus || 'Collective mindfulness',
        sharedIntention: (data as any).sharedIntention || 'Group awareness',
        meditationStyle: (data as any).meditationStyle || 'Traditional mindfulness'
      };
    case 'anxiety':
      return {
        ...baseData,
        groupAnxiety: (data as any).groupAnxiety || 'Shared stress and worry',
        copingTogether: (data as any).copingTogether || 'Mutual support',
        collectiveCalming: (data as any).collectiveCalming || 'Group grounding'
      };
    case 'sleep':
      return {
        ...baseData,
        groupSleepIssues: (data as any).groupSleepIssues || 'Shared sleep challenges',
        relaxationStyle: (data as any).relaxationStyle || 'Gentle and soothing',
        bedtimeEnvironment: (data as any).bedtimeEnvironment || 'Peaceful group space'
      };
    case 'yoga':
    case 'yoga-meditation':
    case 'yoga meditation':
      return {
        ...baseData,
        experienceLevel: (data as any).experienceLevel || 'Mixed experience levels',
        groupSize: (data as any).groupSize || 'Small to medium group',
        yogaStyle: (data as any).yogaStyle || 'Mindful flow',
        specialSubjects: (data as any).specialSubjects || 'General wellness',
        sessionTiming: (data as any).sessionTiming || 'Flexible timing',
        beginningPosition: (data as any).beginningPosition || 'Comfortable seated or lying position',
        focusAreas: (data as any).focusAreas || 'Body awareness and breath',
        instructorDirectives: (data as any).instructorDirectives || 'Gentle guidance and support',
        yogaDirectives: (data as any).yogaDirectives || 'Body-mind connection',
        openingPositions: (data as any).openingPositions || 'Comfortable starting position',
        endingPositions: (data as any).endingPositions || 'Relaxation position'
      };
    case 'motivation':
      return {
        ...baseData,
        groupTypePreference: (data as any).groupType || 'General group',
        collectiveGoal: (data as any).collectiveGoal || 'Shared motivation and progress',
        groupDynamics: (data as any).groupDynamics || 'Collaborative encouragement',
        leadershipStyle: (data as any).leadershipStyle || 'Inclusive and empowering',
        groupChallenges: (data as any).groupChallenges || 'Low morale, unclear goals, or burnout',
        voiceType: (data as any).voiceType || 'Inspiring team leader',
        sessionLength: (data as any).sessionLength || '5 minutes',
        specialOccasions: (data as any).specialOccasions || ''
      };
    default:
      return baseData;
  }
}

function extractIndividualQuestionnaireData(data: QuestionnaireData, categoryName: string) {
  const baseData = {
    specificFocus: data.specificFocus || `Personal ${categoryName.toLowerCase()}`,
    currentFeeling: (data as any).currentFeeling || data.currentState || 'seeking balance',
    primaryGoal: data.primaryGoal || `${categoryName} improvement`,
    desiredFeeling: data.desiredFeeling || 'peaceful and centered',
    triggers: data.triggers || 'daily stress',
    stressors: data.stressors || 'life pressures',
    preferredStyle: data.preferredStyle || 'gentle and supportive',
    voiceType: (data as any).voiceType || 'Calm and grounding',
    sessionLength: data.sessionLength || '5 minutes',
    specialOccasions: (data as any).specialOccasions || ''
  };

  // Category-specific individual fields would be added here
  return baseData;
}

function formatGroupQuestionnaireData(groupData: any, data: QuestionnaireData): string {
  const lines: string[] = [];
  
  // CANONICAL KEYS - These must match what the edge function validation looks for
  if (groupData.collectiveGoal || (data as any).collectiveGoal) {
    lines.push(`Primary Goal: ${groupData.collectiveGoal || (data as any).collectiveGoal}`);
  }
  
  if (groupData.groupChallenges || (data as any).groupChallenges) {
    lines.push(`Specific Focus: ${groupData.groupChallenges || (data as any).groupChallenges}`);
  }
  
  if (groupData.groupDynamics) {
    lines.push(`Group Dynamics: ${groupData.groupDynamics}`);
  }
  
  if (groupData.leadershipStyle) {
    lines.push(`Leadership Style: ${groupData.leadershipStyle}`);
  }
  
  if (groupData.groupTypePreference) {
    lines.push(`Group Type: ${groupData.groupTypePreference}`);
  }
  
  // Keep existing dump for completeness
  const originalLines: string[] = [];
  for (const [key, value] of Object.entries(groupData)) {
    originalLines.push(`- ${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`);
  }
  
  // Add common fields
  originalLines.push(`- Voice tone: ${data.voiceType || 'Calm and grounding'}`);
  originalLines.push(`- Session length: ${data.sessionLength || '10 minutes'}`);
  originalLines.push(`- Environment usage: ${data.useEnvironment ? 'Integrate current environment' : 'Generic setting'}`);
  originalLines.push(`- Background sounds: ${data.backgroundSounds ? 'Include' : 'None'}`);
  if (data.additionalInfo || data.additionalNotes) {
    originalLines.push(`- Additional context: ${data.additionalInfo || data.additionalNotes}`);
  }
  
  // Combine canonical keys with original dump
  return [...lines, '', '--- COMPLETE GROUP DATA ---', ...originalLines].filter(Boolean).join('\n');
}

function formatIndividualQuestionnaireData(individualData: any, data: QuestionnaireData): string {
  const lines = [];
  
  // CRITICAL: Always include required fields in exact format expected by backend validation
  // Backend looks for these specific patterns with regex:
  
  // Primary Goal (always required)
  const primaryGoal = individualData.primaryGoal || data.primaryGoal || 'Personal wellness and balance';
  lines.push(`Primary Goal: ${primaryGoal}`);
  
  // Environment (required format with quotes)
  const environment = data.environment || 'A comfortable, quiet space for relaxation';
  lines.push(`Environment: "${environment}"`);
  
  // Desired Feeling (always required)
  const desiredFeeling = individualData.desiredFeeling || data.desiredFeeling || 'peaceful and centered';
  lines.push(`Desired Feeling: ${desiredFeeling}`);
  
  // Current Feeling/State (always required)
  const currentFeeling = individualData.currentFeeling || (data as any).currentFeeling || data.currentState || 'seeking balance and calm';
  lines.push(`Current Feeling: ${currentFeeling}`);
  
  // Specific Focus (always required)
  const specificFocus = individualData.specificFocus || data.specificFocus || 'Personal relaxation and stress relief';
  lines.push(`Specific Focus: ${specificFocus}`);
  
  // Special Occasions (if provided)
  if ((individualData as any).specialOccasions || (data as any).specialOccasions) {
    const specialOccasions = (individualData as any).specialOccasions || (data as any).specialOccasions;
    lines.push(`Special Occasions: ${specialOccasions}`);
  }
  
  // Additional fields for comprehensive personalization
  if (individualData.triggers || data.triggers) {
    lines.push(`Personal triggers: ${individualData.triggers || data.triggers}`);
  }
  
  if (individualData.stressors || data.stressors) {
    lines.push(`Main stressors: ${individualData.stressors || data.stressors}`);
  }
  
  if (individualData.preferredStyle || data.preferredStyle) {
    lines.push(`Preferred style: ${individualData.preferredStyle || data.preferredStyle}`);
  }
  
  // Add common fields
  lines.push(`Voice tone: ${data.voiceType || 'Calm and grounding'}`);
  lines.push(`Session length: ${data.sessionLength || '10 minutes'}`);
  lines.push(`Background sounds: ${data.backgroundSounds ? 'Include' : 'None'}`);
  
  if (data.additionalInfo || data.additionalNotes) {
    lines.push(`Additional Context: ${data.additionalInfo || data.additionalNotes}`);
  }
  
  return lines.join('\n');
}

// Enhanced prompt generation with comprehensive language variety for all categories
export const createEnhancedPromptWithVariety = (
  categoryName: string,
  basePrompt: string,
  questionnaire: QuestionnaireData,
  variables?: BiographyVariables,
  groupSession?: GroupSession
): string => {
  let prompt = basePrompt;
  
  // Add Zen AF specific enhancements with biographical personalization
  if (categoryName === 'zen-af') {
    // Add dynamic personalization requirements
    prompt += `
BIOGRAPHICAL PERSONALIZATION MANDATORY:
- Mine their biography for all creative content
- Generate original compound insults from their specific life details  
- Create personalized dismissals for their actual toxic relationships
- Use their real work, living, and relationship situations for comedy
- Ensure every creative element serves their specific healing journey`;
    prompt += `\n\nCOMPREHENSIVE LANGUAGE ARSENAL:
- Rotate through ALL therapeutic roast patterns from samples
- Use ALL empowering mantra frameworks, not just favorites
- Draw from ALL creative metaphor structures available
- Employ ALL dismissal pattern varieties for different situations
- Utilize ALL breathing instruction formats for variety
- Reference ALL victory declaration styles for empowerment
- Create NEW combinations of these patterns for originality

BIOGRAPHICAL PERSONALIZATION MANDATE:
- Create original therapeutic roasts from their specific life circumstances
- Generate empowering mantras using their actual personal strengths and victories  
- Build creative metaphors from their real work, relationships, and living situations
- Craft dismissals targeting their actual toxic people and relationship conflicts
- Use their biographical details for breathing instructions and grounding references
- Create victory declarations based on their real achievements and resilience`;
    
    // Add personalized context
    if (variables) {
      const personalizationContext = createZenAfPersonalizationContext(variables, questionnaire);
      prompt = generateOriginalZenAfPrompt(prompt, personalizationContext, variables);
    }
  }
  
  // Add variety instructions for other categories
  else {
    prompt += `\n\nVARIETY REQUIREMENTS:
- Create unique, personalized content based on their specific questionnaire responses
- Avoid generic templates and create fresh approaches for each session
- Draw from diverse therapeutic techniques and language patterns
- Ensure content feels specifically crafted for this individual's needs
- Reference their actual life context and personal details throughout`;
  }
  
  return prompt;
};